import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Activity } from 'types/interfaces/activity';
import { TraderListCollapseApi } from 'hooks/useTraderListCollapse';
import type { ActivityViewMode } from '../ActivityPage';
import { groupActivityByUser } from '../groupActivity';
import ActivityFeedItem from './ActivityFeedItem';
import ActivityTradeGroup from './ActivityTradeGroup';
import './ActivityFeed.css';

/** Grouping axis (independent of the S/M/L view mode). */
export type ActivityGrouping = 'by-card' | 'by-trader';

/** How long a removed tile stays mounted to animate out (matches the CSS exit). */
const EXIT_DURATION_MS = 200;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface FeedEntry {
  activity: Activity;
  exiting: boolean;
}

/**
 * Keeps removed activities mounted briefly so they can animate out instead of
 * popping. Additions/reordering flow straight from `activities`; only removed
 * items are spliced back at their last-known index for `EXIT_DURATION_MS`.
 * When `enabled` is false (grouped view or reduced-motion), removal is instant.
 */
function useFeedRemovalAnimation(activities: Activity[], enabled: boolean): FeedEntry[] {
  const prevRef = useRef<Activity[]>(activities);
  const [exiting, setExiting] = useState<Map<string, { activity: Activity; index: number }>>(
    () => new Map(),
  );
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = activities;
    if (!enabled) return;

    const currentIds = new Set(activities.map((a) => a.id));
    const newlyRemoved = prev
      .map((activity, index) => ({ activity, index }))
      .filter((e) => !currentIds.has(e.activity.id));

    setExiting((prevMap) => {
      let next = prevMap;
      const ensureCopy = () => {
        if (next === prevMap) next = new Map(prevMap);
        return next;
      };
      // Cancel the exit for anything that reappeared (e.g. a re-added card).
      Array.from(prevMap.keys()).forEach((id) => {
        if (currentIds.has(id)) {
          ensureCopy().delete(id);
          const t = timersRef.current.get(id);
          if (t) clearTimeout(t);
          timersRef.current.delete(id);
        }
      });
      // Start the exit for newly-removed items.
      for (const entry of newlyRemoved) {
        const { id } = entry.activity;
        if (next.has(id) || timersRef.current.has(id)) continue;
        ensureCopy().set(id, entry);
        const t = setTimeout(() => {
          timersRef.current.delete(id);
          setExiting((m) => {
            if (!m.has(id)) return m;
            const n = new Map(m);
            n.delete(id);
            return n;
          });
        }, EXIT_DURATION_MS);
        timersRef.current.set(id, t);
      }
      return next;
    });
  }, [activities, enabled]);

  // Drop everything that's exiting the moment the feature is disabled.
  useEffect(() => {
    if (enabled) return;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
    setExiting((m) => (m.size ? new Map() : m));
  }, [enabled]);

  // Clear timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  return useMemo(() => {
    const base: FeedEntry[] = activities.map((activity) => ({ activity, exiting: false }));
    if (!exiting.size) return base;
    // Splice exiting items back at their last-known index (ascending so earlier
    // indices land first and later ones still resolve to sensible slots).
    const sorted = Array.from(exiting.values()).sort((a, b) => a.index - b.index);
    for (const e of sorted) {
      const idx = Math.min(e.index, base.length);
      base.splice(idx, 0, { activity: e.activity, exiting: true });
    }
    return base;
  }, [activities, exiting]);
}

interface ActivityFeedProps {
  activities: Activity[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isFetchingMore: boolean;
  newItemIds: Set<string>;
  clearNewItem: (id: string) => void;
  viewMode: ActivityViewMode;
  grouping: ActivityGrouping;
  /** Opens the compose-trade flow for want_added/available tiles. */
  onSend?: (activity: Activity) => void;
  /** Label of the single active filter — drives the empty-state copy. */
  activeTypeLabel: string;
  /** Saved collapse state for the by-trader lists. */
  collapse: TraderListCollapseApi;
}

const SKELETON_COUNT = 10;

/** Grouped-tile width (px) per image size (Asana 1219008751039721, decision 14). */
export const GROUPED_TILE_WIDTH: Record<string, number> = {
  'images-S': 120,
  'images-M': 148,
  'images-L': 184,
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  isLoading,
  hasMore,
  loadMore,
  isFetchingMore,
  newItemIds,
  clearNewItem,
  viewMode,
  grouping,
  onSend,
  activeTypeLabel,
  collapse,
}) => {
  const isGrouped = grouping === 'by-trader';
  const groups = useMemo(
    () => (isGrouped ? groupActivityByUser(activities) : []),
    [isGrouped, activities],
  );
  // Animate removals only in the flat (by-card) view and when motion is allowed;
  // the grouped view regroups on every change, so it keeps instant removal.
  const [reduceMotion] = useState(prefersReducedMotion);
  const feedEntries = useFeedRemovalAnimation(activities, !isGrouped && !reduceMotion);
  const lastActivityId = activities[activities.length - 1]?.id;
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Infinite scroll via intersection observer on last item
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      if (!node || !hasMore) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
            loadMore();
          }
        },
        { threshold: 0.1 }
      );
      observerRef.current.observe(node);
    },
    [hasMore, loadMore, isFetchingMore]
  );

  // Loading skeleton — card-shaped shimmers in grid layout
  if (isLoading) {
    return (
      <div className={`activity-feed activity-feed--${viewMode}`}>
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <div key={i}>
            <div className="activity-feed__skeleton-card">
              <div className="activity-feed__skeleton-image" />
              <div className="activity-feed__skeleton-footer">
                <div className="activity-feed__skeleton-avatar" />
                <div className="activity-feed__skeleton-lines">
                  <div className="activity-feed__skeleton-text" />
                  <div className="activity-feed__skeleton-text activity-feed__skeleton-text--short" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (activities.length === 0) {
    return (
      <div className="activity-feed activity-feed--empty">
        <div className="activity-feed__empty">
          <p>
            {activeTypeLabel
              ? `No ${activeTypeLabel.toLowerCase()} activity yet. Try another filter.`
              : 'No activity to show. Try another filter.'}
          </p>
        </div>
      </div>
    );
  }

  const feedClassName = `activity-feed activity-feed--${viewMode}`;

  return (
    <>
      {isGrouped ? (
        <div className="activity-groups">
          {groups.map((group, index) => {
            const isLast = index === groups.length - 1;
            return (
              <div key={group.user.id} ref={isLast ? lastItemRef : undefined}>
                <ActivityTradeGroup
                  group={group}
                  tileWidth={GROUPED_TILE_WIDTH[viewMode] ?? 148}
                  onSend={onSend}
                  collapse={collapse}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className={feedClassName}>
          {feedEntries.map(({ activity, exiting }) => {
            // Anchor the infinite-scroll observer to the genuine last item, never an
            // exiting tile (which is about to unmount).
            const isLast = !exiting && activity.id === lastActivityId;
            return (
              <div
                key={activity.id}
                ref={isLast ? lastItemRef : undefined}
              >
                <ActivityFeedItem
                  activity={activity}
                  isNew={newItemIds.has(activity.id)}
                  onAnimationEnd={clearNewItem}
                  onSend={onSend}
                  isExiting={exiting}
                />
              </div>
            );
          })}
        </div>
      )}

      {isFetchingMore && (
        <div className="activity-feed__loading-more">
          <div className="activity-feed__spinner" />
          <span>Loading more activity...</span>
        </div>
      )}

      {!hasMore && activities.length > 0 && (
        <div className="activity-feed__end">
          You've reached the end of the activity feed.
        </div>
      )}
    </>
  );
};

export default ActivityFeed;
